import { useState, useEffect, useRef } from "react";
import { Button, Spin } from "antd";
import { SoundOutlined, ReloadOutlined } from "@ant-design/icons";
import { refreshFacebookMessageAttachments } from "@api/services/facebook";
import { useAppDispatch } from "@stores/redux/store";
import { updateMessageUrl } from "@features/messages/messagesSlice";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";

interface MessageAudioProps {
  src: string;
  messageId?: string;
  conversationId?: string;
  pageId?: string | null;
}

export const MessageAudio = ({
  src,
  messageId,
  conversationId,
  pageId,
}: MessageAudioProps) => {
  const { success, error: showError } = useNotificationContext();
  const [audioError, setAudioError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const audioRef = useRef<HTMLAudioElement>(null);
  const dispatch = useAppDispatch();

  const handleRetry = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!messageId || !pageId) {
      showError("Không tìm thấy thông tin message hoặc page");
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await refreshFacebookMessageAttachments({
        messageId,
        pageId,
      });

      if (response.code === 0) {
        const newUrl = response.data?.newUrl;
        if (newUrl && messageId && conversationId) {
          dispatch(
            updateMessageUrl({
              conversationId,
              messageId,
              newUrl,
            })
          );
          setCurrentSrc(newUrl);
          setAudioError(false);
          setRetryKey((prev) => prev + 1);
          success("Đã tải lại audio thành công");
        } else {
          success("Đang tải lại audio...");
          setAudioError(false);
          setRetryKey((prev) => prev + 1);
        }
      } else {
        showError(response.message || "Tải lại audio thất bại");
      }
    } catch (error) {
      console.error("Error refreshing audio attachments:", error);
      showError("Tải lại audio thất bại");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (src) {
      setCurrentSrc(src);
    }
  }, [src]);

  const handleError = () => {
    setAudioError(true);
    setIsLoading(false);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    setAudioError(false);
  };

  const handleLoadStart = () => {
    setIsLoading(true);
  };

  if (!currentSrc) {
    return (
      <div className={styles.audioPlaceholder}>
        <div className={styles.placeholderIcon}>
          <SoundOutlined style={{ fontSize: 24 }} />
        </div>
        <span className={styles.placeholderText}>Không có file audio</span>
        {messageId && pageId && (
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            className={styles.retryButton}
            size="small"
            onClick={handleRetry}
            loading={isRefreshing}
            disabled={isRefreshing}
          >
            Tải lại
          </Button>
        )}
      </div>
    );
  }

  if (audioError) {
    return (
      <div className={styles.audioPlaceholder}>
        <div className={styles.placeholderIcon}>
          <SoundOutlined style={{ fontSize: 24 }} />
        </div>
        <span className={styles.placeholderText}>Audio đã hết hạn</span>
        {messageId && pageId && (
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            className={styles.retryButton}
            size="small"
            onClick={handleRetry}
            loading={isRefreshing}
            disabled={isRefreshing}
          >
            Tải lại
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={styles.audioWrapper}>
      {isLoading && (
        <div className={styles.audioLoading}>
          <Spin size="small" />
        </div>
      )}
      <audio
        ref={audioRef}
        key={`${currentSrc}-${retryKey}`}
        src={currentSrc}
        controls
        className={styles.audio}
        onError={handleError}
        onCanPlay={handleCanPlay}
        onLoadStart={handleLoadStart}
        preload="metadata"
      />
    </div>
  );
};
